import type { ReactNode } from 'react';
import type { State } from '../api.js';
import { type Band } from '../palette.js';
import { t } from '../i18n.js';

interface HeroProps {
  state: State;
  band: Band;
  onEndOverride: () => void;
  onOpenSettings: () => void;
  onRefresh: () => void;
  refreshDisabled: boolean;
}

export function Hero({ state, onEndOverride, onOpenSettings, onRefresh, refreshDisabled, band }: HeroProps) {
  const z = state.zone;
  const heatingPower = z?.heating_power ?? null;
  const overlayActive = z?.overlay_active === 1;
  const overlayUntil = z?.overlay_until ?? null;
  const heatingPct = heatingPower != null ? Math.round(heatingPower) : null;
  const showBar = band !== 'idle' && heatingPct != null && heatingPct > 0;

  return (
    <section className="hero">
      <div className="hero-top">
        <button
          className="refresh-pill"
          onClick={onRefresh}
          disabled={refreshDisabled}
          aria-label={t('header.refresh')}
          title={refreshDisabled ? t('header.refresh_disabled') : t('header.refresh_title')}
        ><span className="icon">↻</span> {t('header.refresh')}</button>
        <button
          className="hero-gear"
          onClick={onOpenSettings}
          aria-label={t('header.settings')}
          title={t('header.settings')}
        >⚙</button>
      </div>
      <div className="hero-room">{t('hero.room')}</div>
      <div className="hero-temp">
        {z?.current_temp != null ? formatTemp(z.current_temp) : '—'}
      </div>
      <div className="hero-meta">
        {t('hero.target')} <span className="v">{z?.target_temp != null ? `${z.target_temp.toFixed(1)}°` : '—'}</span>
        {overlayActive && overlayUntil != null && (
          <> {t('hero.until')} <span className="v">{formatTime(overlayUntil)}</span></>
        )}
        {overlayActive && (
          <>{' · '}<button className="end-override-link" onClick={onEndOverride}>{t('hero.end_override')}</button></>
        )}
      </div>
      <div className="hero-bar-wrap" data-hidden={!showBar}>
        <div className="hero-bar-fill" style={{ width: `${heatingPct ?? 0}%` }} />
      </div>
      <div className="hero-bar-label" data-hidden={!showBar}>
        {t('hero.heating_label', { n: heatingPct ?? 0 })}
      </div>
    </section>
  );
}

function formatTemp(t: number): ReactNode {
  const rounded = Math.round(t * 10) / 10;
  const sign = rounded < 0 ? '-' : '';
  const abs = Math.abs(rounded);
  const whole = Math.trunc(abs);
  const dec = (abs - whole).toFixed(1).slice(1);
  return <>{sign}{whole}<span className="deg">{dec}°</span></>;
}

function formatTime(unix: number): string {
  const d = new Date(unix * 1000);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}
